import { useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useChild } from "../../../contexts/ChildContext";
import { useCase } from "../../../contexts/CaseContext";
import moment from "moment";
import { calculateAge } from "@visitproof/types";

export const useChildDetail = () => {
  const { childId } = useParams<{ childId: string }>();
  const { cases } = useCase();
  const { selectedChild, setSelectedChild, loading, error } = useChild();
  const lastChildIdRef = useRef<string | null>(null);

  // Memoized case lookup
  const caseInfo = useMemo(() => {
    if (!selectedChild?.caseId || !cases.length) return null;
    return cases.find((c) => c.id === selectedChild.caseId) ?? null;
  }, [selectedChild?.caseId, cases]);

  // Memoized age calculation
  const ageInfo = useMemo(() => {
    if (!selectedChild?.dob) return { formattedDate: "Not provided", age: null };

    const dob = selectedChild.dob.toDate();
    const formattedDate = moment(dob).format("MM/DD/YYYY");
    const age = calculateAge(dob);

    return { formattedDate, age };
  }, [selectedChild?.dob]);

  useEffect(() => {
    // Only call setSelectedChild if the childId actually changed
    if (childId && childId !== lastChildIdRef.current) {
      lastChildIdRef.current = childId;
      setSelectedChild(childId);
    }
  }, [childId, setSelectedChild]);

  return {
    selectedChild,
    loading,
    error,
    caseInfo,
    ageInfo,
  };
}; 